import { MasterPreset, MASTER_PRESETS } from "./masterPresets";

const STORAGE_KEY = "instrument_master_presets";

export interface StoredPreset extends MasterPreset {
  savedAt: number; // Timestamp when preset was saved
}

/**
 * Save a preset to localStorage
 */
export const savePresetToStorage = (preset: MasterPreset): void => {
  try {
    const storedPresets = loadPresetsFromStorage();
    const storedPreset: StoredPreset = {
      ...preset,
      savedAt: Date.now(),
    };
    
    // Check if preset with same ID already exists
    const existingIndex = storedPresets.findIndex((p) => p.id === preset.id);
    if (existingIndex >= 0) {
      storedPresets[existingIndex] = storedPreset;
    } else {
      storedPresets.push(storedPreset);
    }

    localStorage.setItem(STORAGE_KEY, JSON.stringify(storedPresets));
  } catch (error) {
    console.error("Error saving preset to localStorage:", error);
    throw error;
  }
};

/**
 * Load all saved presets from localStorage
 */
export const loadPresetsFromStorage = (): StoredPreset[] => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) {
      return [];
    }
    return JSON.parse(stored) as StoredPreset[];
  } catch (error) {
    console.error("Error loading presets from localStorage:", error);
    return [];
  }
};

/**
 * Delete a preset from localStorage
 */
export const deletePresetFromStorage = (presetId: string): void => {
  try {
    const storedPresets = loadPresetsFromStorage();
    const filtered = storedPresets.filter((p) => p.id !== presetId);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(filtered));
  } catch (error) {
    console.error("Error deleting preset from localStorage:", error);
    throw error;
  }
};

/**
 * Get a single preset by ID from localStorage
 */
export const getPresetFromStorage = (presetId: string): StoredPreset | undefined => {
  try {
    const storedPresets = loadPresetsFromStorage();
    return storedPresets.find((p) => p.id === presetId);
  } catch (error) {
    console.error("Error getting preset from localStorage:", error);
    return undefined;
  }
};

/**
 * Merge built-in presets with custom presets from localStorage
 * Returns an object with all presets (built-in + custom)
 */
export const getAllPresets = (): { [key: string]: MasterPreset } => {
  const customPresets = loadPresetsFromStorage();
  const allPresets = { ...MASTER_PRESETS };
  
  // Add custom presets to the merged object
  customPresets.forEach((preset) => {
    allPresets[preset.id] = preset;
  });
  
  return allPresets;
};

/**
 * Get a preset by ID from either built-in or custom presets
 */
export const getPresetById = (presetId: string): MasterPreset | undefined => {
  // Check built-in presets first
  if (MASTER_PRESETS[presetId]) {
    return MASTER_PRESETS[presetId];
  }
  
  // Check custom presets
  return getPresetFromStorage(presetId);
};
